import { useEffect, useState } from 'react';
import { envoisApi } from '../../api/resources';
import { _parseDateFR } from '../../lib/dates';
import { localeDates, tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

/** Comment l'écran nomme chaque canal de la relance automatique. */
const PAR = {
  EMAIL: '✉️ e-mail',
  SMS: '💬 SMS',
  LES_DEUX: '✉️ e-mail + 💬 SMS',
};

/**
 * Le jour, écrit comme sur le reste de la fiche.
 *
 * @param {Date} d jour
 * @returns {string} la date écrite
 */
const ecrit = (d) =>
  d.toLocaleDateString(localeDates(), { day: '2-digit', month: '2-digit', year: 'numeric' });

/**
 * La relance d'une offre restée sans réponse, sur sa fiche : faite, prévue
 * (le jour où le serveur l'enverra seul) ou éteinte dans Réglages.
 *
 * <p>Le jour prévu se compte comme au serveur : le dernier envoi de l'offre
 * au journal, sinon sa date, plus le délai choisi dans Réglages.
 *
 * @param {object} props propriétés
 * @param {object} props.offre l'offre affichée (en attente)
 * @param {object|null} props.reglages réglages des envois automatiques
 * @param {number} [props.version] change après un envoi, pour relire le journal
 * @param {(page: string) => void} [props.onNav] ouvre Réglages
 * @returns {import('react').ReactElement|null} la carte
 */
export function RelanceOffre({ offre, reglages, version, onNav }) {
  const [envoyeLe, setEnvoyeLe] = useState(null);
  const id = offre?.id;
  useEffect(() => {
    if (!Number.isInteger(id)) return undefined;
    let annule = false;
    envoisApi
      .journal({ size: 200 })
      .then((page) => {
        const envoi = (page?.content || page || []).find(
          (e) => e.type === 'DEVIS' && e.reference === id && e.statut === 'ENVOYE',
        );
        if (!annule) setEnvoyeLe(envoi ? new Date(envoi.envoyeLe) : null);
      })
      .catch(() => {});
    return () => {
      annule = true;
    };
  }, [id, version]);
  if (!offre || !reglages) return null;

  const active = !!reglages.relanceDevisActive;
  const canal = reglages.relanceDevisCanal || 'EMAIL';
  let ligne;
  let couleur = C.navy;
  if (offre.relanceLe) {
    ligne = trf('✅ Relancée le {0}', offre.relanceLe);
    couleur = C.green;
  } else if (!active) {
    ligne = tr('Relance automatique éteinte');
    couleur = C.muted;
  } else {
    const depart = envoyeLe || _parseDateFR(offre.date);
    const jour = depart ? new Date(depart) : new Date();
    jour.setHours(0, 0, 0, 0);
    jour.setDate(jour.getDate() + (Number(reglages.relanceDevisJours) || 15));
    const auj = new Date();
    auj.setHours(0, 0, 0, 0);
    let quand = jour < auj ? auj : jour;
    // Le passage du jour déjà fait, la relance part le lendemain.
    const traite = reglages.dernierTraitement
      ? new Date(reglages.dernierTraitement + 'T00:00')
      : null;
    if (traite && quand <= traite) {
      quand = new Date(traite);
      quand.setDate(quand.getDate() + 1);
    }
    const heure = reglages.heureEnvoi || '08:00';
    ligne =
      canal === 'WHATSAPP'
        ? trf(
            '🟢 Le {0}, elle passera dans « À relancer » avec le message WhatsApp prêt',
            ecrit(quand),
          )
        : trf(
            '⏰ Relance automatique le {0} à {1} par {2}',
            ecrit(quand),
            heure,
            tr(PAR[canal] || PAR.EMAIL),
          );
  }

  return (
    <div
      data-testid="relance-offre"
      style={{
        border: '1px solid ' + C.border,
        borderRadius: 10,
        padding: '9px 11px',
        margin: '0 0 10px',
        fontSize: 10.5,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 3 }}>🔔 {tr('Relance')}</div>
      <div style={{ fontWeight: 700, color: couleur }}>{ligne}</div>
      {!offre.relanceLe && !active && onNav && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => onNav('reglages')}
          onKeyDown={(e) => e.key === 'Enter' && onNav('reglages')}
          style={{ fontSize: 9.5, color: C.navy, fontWeight: 700, cursor: 'pointer', marginTop: 4 }}
        >
          {tr("L'activer dans Réglages › Envois automatiques")} ›
        </div>
      )}
    </div>
  );
}
